import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import '../shared.css';
import './SchemaEditorModal.css';

interface PropertyInfo {
  name: string;
  type: string;
  description?: string;
}

interface NodeInfo {
  label: string;
  description?: string;
  properties: PropertyInfo[];
}

interface EdgeInfo {
  type: string;
  description?: string;
  properties: PropertyInfo[];
}

interface SchemaEditorModalProps {
  kgId: string;
  versionId: string;
  nodes: NodeInfo[];
  edges: EdgeInfo[];
  customAttributes: any;
  onClose: () => void;
  onSave: () => void;
}

interface DescriptionState {
  nodes: { [label: string]: { [field: string]: string } };
  edges: { [type: string]: { [field: string]: string } };
}

const SchemaEditorModal: React.FC<SchemaEditorModalProps> = ({
  kgId,
  versionId,
  nodes,
  edges,
  customAttributes,
  onClose,
  onSave,
}) => {
  const [searchableFields, setSearchableFields] = useState<{ [nodeLabel: string]: Set<string> }>({});
  const [descriptions, setDescriptions] = useState<DescriptionState>({ nodes: {}, edges: {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'nodes' | 'edges'>('nodes');

  useEffect(() => {
    const search = customAttributes._search || {};
    const initialSearchable: { [nodeLabel: string]: Set<string> } = {};
    Object.keys(search).forEach(nodeLabel => {
      initialSearchable[nodeLabel] = new Set(search[nodeLabel] || []);
    });
    setSearchableFields(initialSearchable);

    const nodeDescs = customAttributes.node_descriptions || {};
    const edgeDescs = customAttributes.edge_descriptions || {};

    const initNodeDescs: { [label: string]: { [field: string]: string } } = {};
    nodes.forEach(node => {
      const desc = nodeDescs[node.label] || {};
      initNodeDescs[node.label] = { __description: desc.__description || '' };
      node.properties.forEach(prop => {
        initNodeDescs[node.label][prop.name] = desc[prop.name] || '';
      });
    });

    const initEdgeDescs: { [type: string]: { [field: string]: string } } = {};
    edges.forEach(edge => {
      const desc = edgeDescs[edge.type] || {};
      initEdgeDescs[edge.type] = { __description: desc.__description || '' };
      edge.properties.forEach(prop => {
        initEdgeDescs[edge.type][prop.name] = desc[prop.name] || '';
      });
    });

    setDescriptions({ nodes: initNodeDescs, edges: initEdgeDescs });
  }, [customAttributes, nodes, edges]);

  const toggleSearchable = (nodeLabel: string, propertyName: string) => {
    setSearchableFields(prev => {
      const next = { ...prev };
      if (!next[nodeLabel]) {
        next[nodeLabel] = new Set();
      } else {
        next[nodeLabel] = new Set(next[nodeLabel]);
      }
      if (next[nodeLabel].has(propertyName)) {
        next[nodeLabel].delete(propertyName);
      } else {
        next[nodeLabel].add(propertyName);
      }
      return next;
    });
  };

  const updateDescription = (
    type: 'nodes' | 'edges',
    entityName: string,
    field: string,
    value: string
  ) => {
    setDescriptions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [entityName]: {
          ...prev[type][entityName],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const response: any = await apiService.getCustomAttributes(kgId, versionId);
      const latestAttrs = response.data?.attributes?.custom_attributes || {};
      const updatedAttrs = JSON.parse(JSON.stringify(latestAttrs));

      const searchObj: { [nodeLabel: string]: string[] } = {};
      Object.keys(searchableFields).forEach(nodeLabel => {
        const fields = Array.from(searchableFields[nodeLabel]);
        if (fields.length > 0) {
          searchObj[nodeLabel] = fields;
        }
      });
      updatedAttrs._search = searchObj;

      if (!updatedAttrs.node_descriptions) updatedAttrs.node_descriptions = {};
      Object.keys(descriptions.nodes).forEach(label => {
        if (!updatedAttrs.node_descriptions[label]) updatedAttrs.node_descriptions[label] = {};
        Object.keys(descriptions.nodes[label]).forEach(field => {
          updatedAttrs.node_descriptions[label][field] = descriptions.nodes[label][field];
        });
      });

      if (!updatedAttrs.edge_descriptions) updatedAttrs.edge_descriptions = {};
      Object.keys(descriptions.edges).forEach(type => {
        if (!updatedAttrs.edge_descriptions[type]) updatedAttrs.edge_descriptions[type] = {};
        Object.keys(descriptions.edges[type]).forEach(field => {
          updatedAttrs.edge_descriptions[type][field] = descriptions.edges[type][field];
        });
      });

      await apiService.updateCustomAttributes(kgId, versionId, updatedAttrs);
      onSave();
      onClose();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to save schema editor changes'));
      console.error('Error saving schema editor:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content schema-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Schema Editor</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message" style={{ margin: '0 24px' }}>{error}</div>}

        <div className="schema-editor-body">
          <div className="schema-editor-tabs-bar">
            <div className="schema-editor-tabs">
              <button
                className={`schema-editor-tab ${activeTab === 'nodes' ? 'active' : ''}`}
                onClick={() => setActiveTab('nodes')}
              >
                Nodes ({nodes.length})
              </button>
              <button
                className={`schema-editor-tab ${activeTab === 'edges' ? 'active' : ''}`}
                onClick={() => setActiveTab('edges')}
              >
                Edges ({edges.length})
              </button>
            </div>
          </div>

          <div className="se-sheet-wrap">
            {activeTab === 'nodes' && (
              nodes.length === 0 ? (
                <div className="empty-state">No nodes available in schema.</div>
              ) : (
                <table className="se-sheet">
                  <thead>
                    <tr>
                      <th className="se-col-search">Search</th>
                      <th className="se-col-prop">Property</th>
                      <th className="se-col-type">Type</th>
                      <th className="se-col-desc">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodes.map(node => (
                      <React.Fragment key={node.label}>
                        {/* Entity header row — name in Property col, desc in Description col */}
                        <tr className="se-entity-row se-entity-node">
                          <td className="se-entity-spacer"></td>
                          <td className="se-entity-name-cell">{node.label}</td>
                          <td className="se-entity-spacer"></td>
                          <td className="se-entity-desc-cell">
                            <input
                              type="text"
                              className="se-entity-desc-input"
                              value={descriptions.nodes[node.label]?.__description || ''}
                              onChange={(e) => updateDescription('nodes', node.label, '__description', e.target.value)}
                              placeholder="Entity description..."
                            />
                          </td>
                        </tr>
                        {/* Property rows */}
                        {node.properties.map(prop => (
                          <tr key={`${node.label}-${prop.name}`} className="se-prop-row">
                            <td className="se-cell-center">
                              <input
                                type="checkbox"
                                className="se-checkbox"
                                checked={searchableFields[node.label]?.has(prop.name) || false}
                                onChange={() => toggleSearchable(node.label, prop.name)}
                              />
                            </td>
                            <td className="se-cell-prop">{prop.name}</td>
                            <td className="se-cell-type">
                              <span className={`se-type type-${prop.type.toLowerCase()}`}>{prop.type}</span>
                            </td>
                            <td className="se-cell-desc">
                              <input
                                type="text"
                                className="se-desc-input"
                                value={descriptions.nodes[node.label]?.[prop.name] || ''}
                                onChange={(e) => updateDescription('nodes', node.label, prop.name, e.target.value)}
                                placeholder="--"
                              />
                            </td>
                          </tr>
                        ))}
                        {node.properties.length === 0 && (
                          <tr className="se-prop-row se-empty-row">
                            <td colSpan={4} className="se-cell-empty">No properties</td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )
            )}

            {activeTab === 'edges' && (
              edges.length === 0 ? (
                <div className="empty-state">No edges available in schema.</div>
              ) : (
                <table className="se-sheet">
                  <thead>
                    <tr>
                      <th className="se-col-prop">Property</th>
                      <th className="se-col-type">Type</th>
                      <th className="se-col-desc">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {edges.map(edge => (
                      <React.Fragment key={edge.type}>
                        <tr className="se-entity-row se-entity-edge">
                          <td className="se-entity-name-cell">{edge.type}</td>
                          <td className="se-entity-spacer"></td>
                          <td className="se-entity-desc-cell">
                            <input
                              type="text"
                              className="se-entity-desc-input"
                              value={descriptions.edges[edge.type]?.__description || ''}
                              onChange={(e) => updateDescription('edges', edge.type, '__description', e.target.value)}
                              placeholder="Entity description..."
                            />
                          </td>
                        </tr>
                        {edge.properties.map(prop => (
                          <tr key={`${edge.type}-${prop.name}`} className="se-prop-row">
                            <td className="se-cell-prop">{prop.name}</td>
                            <td className="se-cell-type">
                              <span className={`se-type type-${prop.type.toLowerCase()}`}>{prop.type}</span>
                            </td>
                            <td className="se-cell-desc">
                              <input
                                type="text"
                                className="se-desc-input"
                                value={descriptions.edges[edge.type]?.[prop.name] || ''}
                                onChange={(e) => updateDescription('edges', edge.type, prop.name, e.target.value)}
                                placeholder="--"
                              />
                            </td>
                          </tr>
                        ))}
                        {edge.properties.length === 0 && (
                          <tr className="se-prop-row se-empty-row">
                            <td colSpan={3} className="se-cell-empty">No properties</td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="tab-btn tab-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="tab-btn tab-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchemaEditorModal;
